import React from "react";
import LegalPage from "./LegalPage";
import { ShieldCheck } from "lucide-react";
import { EMAIL, PHONE_DISPLAY } from "../data";

const H = ({ children }) => <h2 className="font-heading font-normal text-xl text-[#172B38] mt-8 mb-2">{children}</h2>;
const P = ({ children }) => <p>{children}</p>;
const UL = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1">
    {items.map((it) => <li key={it}>{it}</li>)}
  </ul>
);

export default function Privacy() {
  return (
    <LegalPage title="Privacy Policy" updated="21 July, 2026" updatedLabel="Effective Date">
      <P>
        Honest Water LLC ("Honest Water LLC," "we," "our," or "us") is committed to protecting your privacy. This
        Privacy Policy describes what information we collect, how we use it, and the choices you have regarding your
        personal information when you use our website or communicate with us.
      </P>

      <H>1. What Information Is Collected</H>
      <P>Honest Water LLC may collect information you voluntarily provide, including:</P>
      <UL items={[
        "Full name",
        "Phone number",
        "Email address",
        "Property/service address",
        "Appointment details",
        "Service requests",
        "Information submitted through contact forms",
      ]} />
      <P>
        We may also automatically collect limited technical information, including your IP address, browser type,
        device information, pages visited, and website usage analytics.
      </P>

      <H>2. How User Data Is Used</H>
      <P>Collected information is used only to:</P>
      <UL items={[
        "Respond to inquiries",
        "Schedule appointments",
        "Provide requested services",
        "Send estimates and invoices",
        "Communicate about appointments and customer support",
        "Improve the website and customer experience",
        "Comply with legal obligations",
      ]} />
      <P>
        <strong>
          Honest Water LLC does not sell, rent, trade, or share customer personal information with third parties for
          marketing or promotional purposes.
        </strong>
      </P>

      <H>3. SMS Opt-In Details</H>
      <P>Customers who opt in may receive SMS messages regarding:</P>
      <UL items={[
        "Appointment reminders",
        "Appointment confirmations",
        "Service updates",
        "Customer support",
        "Important account notifications",
      ]} />
      <UL items={[
        "SMS consent is voluntary.",
        "Reply STOP to opt out.",
        "Reply HELP for assistance.",
        "Message and data rates may apply.",
        "Consent is not a condition of purchasing services.",
      ]} />

      <H>4. Cookie & Tracking Practices</H>
      <P>We use cookies and similar technologies only to:</P>
      <UL items={[
        "Improve website performance",
        "Remember preferences",
        "Analyze website traffic",
        "Enhance user experience",
      ]} />
      <P>You may disable cookies through your browser settings.</P>

      <H>5. Data Security and Handling</H>
      <P>
        Honest Water LLC uses reasonable administrative, technical, and physical safeguards to protect personal
        information from unauthorized access, disclosure, alteration, or destruction.
      </P>
      <UL items={[
        "Access is limited to authorized personnel.",
        "Information is retained only as long as necessary or required by law.",
        "Although reasonable security measures are implemented, no internet transmission is 100% secure.",
      ]} />

      <H>6. User Rights</H>
      <P>Users may:</P>
      <UL items={[
        "Request access to their personal information",
        "Request corrections",
        "Request deletion where applicable",
        "Withdraw consent",
        "Opt out of SMS communications at any time",
        "Contact Honest Water LLC regarding privacy questions",
      ]} />

      <H>7. Mobile Information Sharing Statement</H>
      <div className="rounded-none border-l-4 border-[#172B38] bg-[#F4F1E9] p-5 md:p-6 my-3" data-testid="mobile-sharing-box">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-[#172B38] shrink-0 mt-0.5" />
          <div className="space-y-3">
            <p className="font-semibold text-[#172B38]">
              No mobile information will be shared with third parties or affiliates for marketing or promotional
              purposes.
            </p>
            <p className="font-semibold text-[#172B38]">
              Text messaging originator opt-in data and consent will never be shared with any third parties under any
              circumstances.
            </p>
            <p className="text-slate-700">
              Any limited information sharing is only permitted with trusted service providers performing services on
              behalf of Honest Water LLC under strict confidentiality agreements, or when required by law, and never
              for advertising or marketing purposes.
            </p>
          </div>
        </div>
      </div>

      <H>Changes to This Privacy Policy</H>
      <P>
        Honest Water LLC may update this Privacy Policy from time to time. Any revisions will be posted on this page
        with an updated effective date. We encourage you to review this page periodically to stay informed about how we
        protect your information.
      </P>

      <H>Contact Us</H>
      <P>
        If you have any questions about this Privacy Policy or our privacy practices, please contact Honest Water LLC
        using the contact information available on our website:
      </P>
      <P>Email: <a href={`mailto:${EMAIL}`} className="text-[#172B38] underline">{EMAIL}</a></P>
      <P>Phone: {PHONE_DISPLAY}</P>
    </LegalPage>
  );
}
